import React, { useMemo } from "react";
import { motion } from "framer-motion";
import ReactApexChart from "react-apexcharts";
import { Table, Button, Badge, Tooltip } from "flowbite-react";
import { Link } from "react-router-dom";


// =============================
// MOCK DATA (substitua depois)
// =============================
const MOCK = {
  kpis: {
    openDeals: 92,
    pipelineValue: 385000,
    forecast30d: 214000,
    winRate90d: 27,
    avgCycleDays: 23,
    activitiesToday: 58,
  },
  pipelineStages: [
    { name: "Prospect", value: 68000, qty: 34 },
    { name: "Qualificação", value: 92000, qty: 28 },
    { name: "Apresentação", value: 76000, qty: 18 },
    { name: "Proposta", value: 92000, qty: 10 },
    { name: "Fechamento", value: 57000, qty: 6 },
  ],
  velocity: [
    { week: "Sem 1", days: 5.2 },
    { week: "Sem 2", days: 4.8 },
    { week: "Sem 3", days: 4.1 },
    { week: "Sem 4", days: 3.9 },
  ],
  forecastWeekly: [
    { week: "Sem 1", value: 52000 },
    { week: "Sem 2", value: 56000 },
    { week: "Sem 3", value: 58000 },
    { week: "Sem 4", value: 48000 },
  ],
  leaderboard: [
    { user: "Laura", won: 8, value: 112000 },
    { user: "Marcos", won: 6, value: 86000 },
    { user: "Ana", won: 5, value: 69000 },
  ],
  followUps: [
    {
      id: 1,
      account: "Nimbus",
      owner: "Laura",
      due: "21/09/25 14:30",
      type: "Call",
    },
    {
      id: 2,
      account: "Vortex",
      owner: "Marcos",
      due: "21/09/25 16:00",
      type: "Email",
    },
    {
      id: 3,
      account: "Orion",
      owner: "Ana",
      due: "22/09/25 10:00",
      type: "Demo",
    },
  ],
  sources: [
    { name: "Inbound", value: 42 },
    { name: "Outbound", value: 31 },
    { name: "Parcerias", value: 18 },
    { name: "Eventos", value: 9 },
  ],
  risks: [
    {
      id: 1,
      account: "Atlas",
      stage: "Proposta",
      reason: "Sem resposta 7d",
      value: 28000,
    },
    {
      id: 2,
      account: "Neon",
      stage: "Apresentação",
      reason: "Stakeholder ausente",
      value: 18000,
    },
    {
      id: 3,
      account: "Zeta",
      stage: "Qualificação",
      reason: "Budget incerto",
      value: 12000,
    },
  ],
};

// =============================
// Helpers
// =============================
const currency = (n) =>
  (n ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

// =============================
// Component
// =============================
export default function DashboardCRMNew() {
  const brand = "#F2EDE8";

  // ----- Charts: Pipeline por estágio (barra horizontal)
  const stageOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: {
        bar: { horizontal: true, borderRadius: 6, barHeight: "68%" },
      },
      grid: { borderColor: "#EEF2F6" },
      dataLabels: { enabled: false },
      xaxis: {
        categories: MOCK.pipelineStages.map((s) => s.name),
        labels: { style: { fontSize: "12px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      colors: [brand],
      tooltip: { y: { formatter: (val) => currency(val) } },
    }),
    []
  );
  const stageSeries = useMemo(
    () => [{ name: "Valor", data: MOCK.pipelineStages.map((s) => s.value) }],
    []
  );

  // ----- Charts: Forecast semanal (área)
  const forecastOptions = useMemo(
    () => ({
      chart: { type: "area", toolbar: { show: false } },
      stroke: { curve: "smooth", width: 3 },
      fill: {
        type: "gradient",
        gradient: { opacityFrom: 0.3, opacityTo: 0.06 },
      },
      grid: { borderColor: "#EEF2F6" },
      dataLabels: { enabled: false },
      xaxis: {
        categories: MOCK.forecastWeekly.map((w) => w.week),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: "12px" } },
      },
      colors: [brand],
      tooltip: { y: { formatter: (val) => currency(val) } },
    }),
    []
  );
  const forecastSeries = useMemo(
    () => [
      {
        name: "Receita prevista",
        data: MOCK.forecastWeekly.map((w) => w.value),
      },
    ],
    []
  );

  // ----- Charts: Velocidade (dias por estágio) — linha
  const velocityOptions = useMemo(
    () => ({
      chart: { type: "line", toolbar: { show: false } },
      stroke: { curve: "smooth", width: 3 },
      markers: { size: 4 },
      grid: { borderColor: "#EEF2F6" },
      dataLabels: { enabled: false },
      xaxis: {
        categories: MOCK.velocity.map((w) => w.week),
        labels: { style: { fontSize: "12px" } },
      },
      yaxis: { labels: { formatter: (v) => `${v}d` } },
      colors: [brand],
      tooltip: { y: { formatter: (val) => `${val} dias` } },
    }),
    []
  );
  const velocitySeries = useMemo(
    () => [
      { name: "Velocidade média", data: MOCK.velocity.map((w) => w.days) },
    ],
    []
  );

  // ----- Charts: Fontes de lead (rosca)
  const sourcesOptions = useMemo(
    () => ({
      chart: { type: "donut" },
      labels: MOCK.sources.map((s) => s.name),
      legend: { position: "bottom" },
      stroke: { width: 2 },
      colors: [brand, "#9CA3AF", "#F59E0B", "#10B981"],
      dataLabels: { enabled: false },
    }),
    []
  );
  const sourcesSeries = useMemo(() => MOCK.sources.map((s) => s.value), []);

  return (
    <div className="space-y-5">
      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
      >
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
            Amplia Capital — Painel
          </h1>
          <p className="text-sm text-gray-600">
            Visão executiva do pipeline e desempenho comercial
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select className="border-gray-200 rounded-lg text-sm">
            <option>Últimos 30 dias</option>
            <option>Este mês</option>
            <option>Últimos 90 dias</option>
          </select>
          <select className="border-gray-200 rounded-lg text-sm">
            <option>Todos os times</option>
            <option>Enterprise</option>
            <option>SMB</option>
          </select>
          <Button color="light" className="!border-gray-300">
            Exportar
          </Button>
        </div>
      </motion.div>

      {/* KPIs GRID */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.05 }}
        className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3"
      >
        {[
          { label: "Deals em aberto", value: MOCK.kpis.openDeals },
          {
            label: "Valor no pipeline",
            value: currency(MOCK.kpis.pipelineValue),
          },
          { label: "Forecast (30d)", value: currency(MOCK.kpis.forecast30d) },
          { label: "Win rate (90d)", value: `${MOCK.kpis.winRate90d}%` },
          { label: "Ciclo médio", value: `${MOCK.kpis.avgCycleDays} dias` },
          { label: "Atividades hoje", value: MOCK.kpis.activitiesToday },
        ].map((k, i) => (
          <div
            key={i}
            className="bg-white rounded-xl border border-gray-200 shadow-sm p-4"
          >
            <p className="text-xs text-gray-500 font-medium">{k.label}</p>
            <p className="text-2xl font-extrabold text-gray-900 mt-1">
              {k.value}
            </p>
            <div className="mt-2 h-1 rounded bg-gray-100">
              <div
                className="h-1 rounded"
                style={{ width: `${60 + i * 5}%`, backgroundColor: brand }}
              />
            </div>
          </div>
        ))}
      </motion.div>

      {/* LINHA 1: Pipeline por Estágio + Forecast */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Pipeline por estágio
              </h2>
              <p className="text-xs text-gray-500">
                Onde está concentrado o valor
              </p>
            </div>
            <Badge color="pink" className="!bg-[#A60B3E]">
              R$
            </Badge>
          </div>
          <ReactApexChart
            options={stageOptions}
            series={stageSeries}
            type="bar"
            height={360}
          />
          <div className="grid grid-cols-5 gap-2 mt-2">
            {MOCK.pipelineStages.map((s, idx) => (
              <div
                key={idx}
                className="text-center border border-gray-100 rounded-lg p-2"
              >
                <p className="text-[11px] text-gray-500">{s.name}</p>
                <p className="text-sm font-bold text-gray-800">{s.qty} deals</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Forecast — próximas 4 semanas
              </h2>
              <p className="text-xs text-gray-500">
                Receita prevista por semana
              </p>
            </div>
            <Badge color="pink" className="!bg-[#A60B3E]">
              30d
            </Badge>
          </div>
          <ReactApexChart
            options={forecastOptions}
            series={forecastSeries}
            type="area"
            height={360}
          />
          <div className="text-xs text-gray-500 mt-2">
            Baseado em probabilidade por estágio e histórico
          </div>
        </div>
      </div>

      {/* LINHA 2: Velocidade + Fontes + Alertas de Risco */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-2">
            Velocidade do pipeline
          </h2>
          <ReactApexChart
            options={velocityOptions}
            series={velocitySeries}
            type="line"
            height={280}
          />
          <div className="text-xs text-gray-500 mt-2">
            Dias médios por etapa (quanto menor, melhor)
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-2">
            Fontes de lead
          </h2>
          <ReactApexChart
            options={sourcesOptions}
            series={sourcesSeries}
            type="donut"
            height={280}
          />
          <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
            {MOCK.sources.map((s, i) => (
              <div
                key={i}
                className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2"
              >
                <span className="text-gray-700">{s.name}</span>
                <span className="font-bold text-gray-900">{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-3">
            Alertas de risco
          </h2>
          <div className="space-y-2">
            {MOCK.risks.map((r) => (
              <div
                key={r.id}
                className="border border-rose-100 bg-rose-50/50 rounded-xl p-3"
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-gray-900">{r.account}</div>
                  <Badge color="pink" className="!bg-[#A60B3E] !text-white">
                    {r.stage}
                  </Badge>
                </div>
                <div className="text-xs text-gray-600 mt-1">{r.reason}</div>
                <div className="text-sm font-bold text-gray-900 mt-1">
                  {currency(r.value)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* LINHA 3: Ranking + Próximos Follow-ups */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 xl:col-span-1">
          <h2 className="text-lg font-bold text-gray-900 mb-2">
            Ranking (últimos 90d)
          </h2>
          <div className="space-y-2">
            {MOCK.leaderboard.map((u, i) => (
              <div
                key={i}
                className="flex items-center justify-between border border-gray-100 rounded-xl p-3"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 grid place-items-center rounded-full bg-gray-100 font-bold text-gray-800">
                    {u.user.slice(0, 1)}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{u.user}</div>
                    <div className="text-xs text-gray-500">{u.won} ganhos</div>
                  </div>
                </div>
                <div className="font-extrabold text-gray-900">
                  {currency(u.value)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 xl:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">
              Próximos follow-ups
            </h2>
            <Button size="xs" color="light" className="!border-gray-300">
              <Link to="/tasks">Ver tudo</Link>
            </Button>
          </div>
          <div className="rounded-md overflow-hidden border border-gray-100">
            <Table striped>
              <Table.Head>
                <Table.HeadCell>Conta</Table.HeadCell>
                <Table.HeadCell>Responsável</Table.HeadCell>
                <Table.HeadCell>Tipo</Table.HeadCell>
                <Table.HeadCell className="text-right">Quando</Table.HeadCell>
              </Table.Head>
              <Table.Body>
                {MOCK.followUps.map((f) => (
                  <Table.Row key={f.id} className="bg-white">
                    <Table.Cell className="font-medium">{f.account}</Table.Cell>
                    <Table.Cell>{f.owner}</Table.Cell>
                    <Table.Cell>{f.type}</Table.Cell>
                    <Table.Cell className="text-right">{f.due}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
